/**
 * REST helpers for live quiz + grasp (trainer).
 *
 * Flow:
 *   step 1) pushLiveQuiz when trainer clicks Push Pre/Post (not on Complete/Skip)
 *   step 2) fetchLiveQuizScores / fetchGraspVotes for trainer report poll
 */

import { livekitAuthService as authService } from "../utils/authService";
import { singleFlight } from "./singleFlight";
import { extractLiveKitApiErrorMessage } from "./liveKitConnectErrors";
import type {
  ActiveLiveQuiz,
  GraspVote,
  LiveQuizSubmission,
  QuizSlot,
  StudentQuizHistoryItem,
} from "./liveQuizTypes";
import {
  isLiveQuiz,
  normalizeAnswerSlot,
  normalizeGraspVote,
  normalizeLiveQuizQuestion,
} from "./liveQuizTypes";

export type LiveQuizAvailability = {
  topic_id: string;
  slot: QuizSlot;
  needed: number;
  available: number;
  can_push: boolean;
  short: boolean;
};

export const EMPTY_QUIZ_BANK_MESSAGE = "Questions doesn't exist";

export function liveQuizPushErrorMessage(err: unknown): string {
  const apiMsg = extractLiveKitApiErrorMessage(err);
  const raw =
    apiMsg ||
    (err instanceof Error ? err.message : "") ||
    "";
  const unwrapped =
    raw.match(/Validation error for field '[^']+': (.+)/i)?.[1]?.trim() ||
    raw.trim();
  if (unwrapped.includes(EMPTY_QUIZ_BANK_MESSAGE)) {
    return EMPTY_QUIZ_BANK_MESSAGE;
  }
  return unwrapped || "Unable to push quiz";
}

export function normalizeQuiz(
  raw: Record<string, unknown> | null | undefined,
): ActiveLiveQuiz | null {
  // ---------------------------------------------------------------
  // step 1: Normalize snake/camel quiz payload from API
  // ---------------------------------------------------------------
  if (!raw) return null;
  const statusRaw = String(raw.status ?? "").toLowerCase();
  let status: ActiveLiveQuiz["status"] =
    statusRaw === "completed" || statusRaw === "aborted" || statusRaw === "active"
      ? (statusRaw as ActiveLiveQuiz["status"])
      : "completed";
  const quiz: ActiveLiveQuiz = {
    push_id: String(raw.push_id ?? raw.pushId ?? ""),
    slot: (raw.slot === "pre" ? "pre" : "post") as QuizSlot,
    topic_id: String(raw.topic_id ?? raw.topicId ?? ""),
    topic_title: String(raw.topic_title ?? raw.topicTitle ?? "Topic"),
    topic_index: Number(raw.topic_index ?? raw.topicIndex ?? 0),
    questions: Array.isArray(raw.questions)
      ? (raw.questions as Record<string, unknown>[]).map(normalizeLiveQuizQuestion)
      : [],
    duration_seconds: Number(raw.duration_seconds ?? raw.durationSeconds ?? 300),
    pushed_at: Number(raw.pushed_at ?? raw.pushedAt ?? Date.now()),
    server_now_ms: Number(raw.server_now_ms ?? raw.serverNowMs ?? 0) || undefined,
    status,
  };
  // Timer elapsed → never treat as Live (covers stale "active" from API)
  if (quiz.status === "active" && !isLiveQuiz(quiz)) {
    quiz.status = "completed";
  }
  return quiz;
}

export async function pushLiveQuiz(params: {
  sessionId: string;
  topicId: string;
  topicTitle: string;
  topicIndex?: number;
  slot?: QuizSlot;
}): Promise<ActiveLiveQuiz> {
  // ---------------------------------------------------------------
  // step 1: POST push — backend samples questions + stores active quiz
  // ---------------------------------------------------------------
  const res = await authService.post(
    `/session/sessions/${params.sessionId}/live-quiz/push/`,
    {
      topic_id: params.topicId,
      topic_title: params.topicTitle,
      topic_index: params.topicIndex ?? 0,
      slot: params.slot || "post",
    },
  );

  // ---------------------------------------------------------------
  // step 2: Normalize response for LiveKit broadcast
  // ---------------------------------------------------------------
  const quiz = normalizeQuiz(res.data?.quiz);
  if (!quiz) throw new Error("Quiz push returned empty payload");
  return quiz;
}

export async function fetchLiveQuizAvailability(params: {
  sessionId: string;
  topicId: string;
  slot?: QuizSlot;
}): Promise<LiveQuizAvailability> {
  // ---------------------------------------------------------------
  // step 1: GET bank size vs configured count for this topic/slot
  // ---------------------------------------------------------------
  const slot = params.slot || "post";
  const qs = `?topic_id=${encodeURIComponent(params.topicId)}&slot=${encodeURIComponent(slot)}`;
  const res = await authService.get(
    `/session/sessions/${params.sessionId}/live-quiz/availability/${qs}`,
  );
  const data = (res.data || {}) as Record<string, unknown>;
  const available = Math.max(0, Number(data.available ?? 0));
  const needed = Math.max(0, Number(data.needed ?? 0));
  return {
    topic_id: String(data.topic_id ?? params.topicId),
    slot: data.slot === "pre" ? "pre" : "post",
    needed,
    available,
    can_push: Boolean(data.can_push ?? available > 0),
    short: Boolean(data.short ?? (available > 0 && needed > 0 && available < needed)),
  };
}

export async function fetchActiveLiveQuiz(
  sessionId: string,
): Promise<ActiveLiveQuiz | null> {
  // ---------------------------------------------------------------
  // step 1: GET active quiz (late join / refresh)
  // ---------------------------------------------------------------
  return singleFlight(`quiz-active:${sessionId}`, async () => {
    const res = await authService.get(
      `/session/sessions/${sessionId}/live-quiz/active/`,
    );
    return normalizeQuiz(res.data?.quiz);
  });
}

export async function fetchLiveQuizScores(sessionId: string): Promise<{
  activeQuiz: ActiveLiveQuiz | null;
  submissions: LiveQuizSubmission[];
  quizzes: ActiveLiveQuiz[];
}> {
  // ---------------------------------------------------------------
  // step 1: GET trainer scoreboard (+ all session quizzes)
  // ---------------------------------------------------------------
  return singleFlight(`quiz-scores:${sessionId}`, async () => {
  const res = await authService.get(
    `/session/sessions/${sessionId}/live-quiz/scores/`,
  );

  // ---------------------------------------------------------------
  // step 2: Normalize submissions + quiz list
  // ---------------------------------------------------------------
  const submissions = Array.isArray(res.data?.submissions)
    ? res.data.submissions.map((s: Record<string, unknown>) => ({
        push_id: String(s.push_id ?? ""),
        student_id: String(s.student_id ?? ""),
        student_name: String(s.student_name ?? ""),
        answers: Array.isArray(s.answers)
          ? s.answers.map(normalizeAnswerSlot)
          : [],
        score: Number(s.score ?? 0),
        max_score: Number(s.max_score ?? 0),
        submitted_at: Number(s.submitted_at ?? 0),
        timed_out: Boolean(s.timed_out),
      }))
    : [];
  const quizzesRaw = Array.isArray(res.data?.quizzes) ? res.data.quizzes : [];
  const quizzes = quizzesRaw
    .map((q: Record<string, unknown>) => normalizeQuiz(q))
    .filter(
      (q: ActiveLiveQuiz | null): q is ActiveLiveQuiz =>
        Boolean(q?.push_id),
    );
  return {
    activeQuiz: normalizeQuiz(res.data?.active_quiz),
    submissions,
    quizzes,
  };
  });
}

export async function fetchStudentQuizHistory(
  sessionId: string,
  studentId: string,
): Promise<StudentQuizHistoryItem[]> {
  // ---------------------------------------------------------------
  // step 1: One in-flight history request for this student
  // ---------------------------------------------------------------
  return singleFlight(
    `quiz-history:${sessionId}:${studentId}`,
    async () => {
  const res = await authService.get(
    `/session/sessions/${encodeURIComponent(sessionId)}/live-quiz/my-history/?student_id=${encodeURIComponent(studentId)}`,
  );
  const rows = Array.isArray(res.data?.quizzes) ? res.data.quizzes : [];
  return rows.map((s: Record<string, unknown>) => {
    const questions = Array.isArray(s.questions)
      ? (s.questions as Record<string, unknown>[]).map(normalizeLiveQuizQuestion)
      : undefined;
    return {
      push_id: String(s.push_id ?? ""),
      student_id: String(s.student_id ?? studentId),
      student_name: String(s.student_name ?? ""),
      answers: Array.isArray(s.answers)
        ? s.answers.map(normalizeAnswerSlot)
        : [],
      score: Number(s.score ?? 0),
      max_score: Number(s.max_score ?? 0),
      submitted_at: Number(s.submitted_at ?? 0),
      timed_out: Boolean(s.timed_out),
      topic_id: s.topic_id ? String(s.topic_id) : undefined,
      topic_title: s.topic_title ? String(s.topic_title) : "Topic",
      slot: s.slot === "pre" ? "pre" : "post",
      questions,
      missed: Boolean(s.missed),
      quiz_completed: Boolean(s.quiz_completed),
    } as StudentQuizHistoryItem;
  });
    },
  );
}

export async function fetchGraspVotes(
  sessionId: string,
  topicId?: string,
): Promise<{ votes: GraspVote[]; counts: Record<string, number> }> {
  // ---------------------------------------------------------------
  // step 1: GET grasp votes (+ optional topic filter)
  // ---------------------------------------------------------------
  const path = topicId
    ? `/session/sessions/${sessionId}/grasp/?topic_id=${encodeURIComponent(topicId)}`
    : `/session/sessions/${sessionId}/grasp/`;
  return singleFlight(`grasp:${sessionId}:${topicId || ""}`, async () => {
  const res = await authService.get(path);

  // ---------------------------------------------------------------
  // step 2: Normalize votes for trainer absorption panel
  // ---------------------------------------------------------------
  const votes = Array.isArray(res.data?.votes)
    ? (res.data.votes as Record<string, unknown>[])
        .map((v) => normalizeGraspVote(v))
        .filter((v): v is GraspVote => Boolean(v))
    : [];
  return { votes, counts: res.data?.counts || {} };
  });
}

export async function finalizeGraspFeedback(params: {
  sessionId: string;
  topicId: string;
  topicTitle: string;
}): Promise<{ marked_not_answered: number }> {
  const res = await authService.post(
    `/session/sessions/${params.sessionId}/grasp/finalize/`,
    {
      topic_id: params.topicId,
      topic_title: params.topicTitle,
    },
  );
  return {
    marked_not_answered: Number(res.data?.marked_not_answered ?? 0),
  };
}
